/* eslint-disable react/no-array-index-key */
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

import type { VARIANTS } from './constants'
import { VARIANT_SIMPLE } from './constants'

import { calculateDiscount } from 'client/utils'

import CheckoutWalletSelector from 'src/comps/CheckoutWalletSelector'

import IconHelp from 'client/icons/Help'
import Price from 'client/components/Price'
import MessageBox from 'src/comps/MessageBox'
import styles from './styles.module.css'

import {
  Amount,
  Line,
  LineAdditionalFeesBase,
  LineRoomResume,
  PriceDetailsBase,
  TaxesModalWrapper,
  TaxesWrapper,
  Title,
  DiscountText,
  Note,
  PromoText,
} from './styles'

import { BottomSheet } from '@s4l/ds'
import { useUseCase } from '@s4l/utils/hooks/useUseCase'

export type AdditionalFeesType = {
  amount: number
  currency: string
  client?: { amount: number; currency: string }
  local?: { amount: number; currency: string }
}

interface LineTaxesProps {
  taxes: number
  taxesCurrency: string
}

export const LineTaxes = ({ taxes, taxesCurrency }: LineTaxesProps) => {
  const intl = useIntl()

  return taxes ? (
    <Line data-testid="taxes">
      <TaxesWrapper>
        {intl.formatMessage({ id: 'global.taxes-fees' })}
        <BottomSheet>
          <BottomSheet.Trigger className={styles.trigger}>
            <IconHelp size={20} />
          </BottomSheet.Trigger>
          <BottomSheet.Content
            title={intl.formatMessage({ id: 'global.taxes-fees' })}
            data-testid="taxes-modal"
            size="s"
            modalOnDesktop
          >
            <TaxesModalWrapper>
              <p>{intl.formatMessage({ id: 'global.taxes-fees-information' })}</p>
            </TaxesModalWrapper>
          </BottomSheet.Content>
        </BottomSheet>
      </TaxesWrapper>

      <Amount>
        <Price amount={taxes} currency={taxesCurrency} />
      </Amount>
    </Line>
  ) : null
}

export interface LineRoomsProps {
  nights: number
  subtotal: number
  subtotalCurrency: string
  showDiscount?: boolean
}

const LineRoom = ({ nights, subtotal, subtotalCurrency, showDiscount = true }: LineRoomsProps) => {
  const intl = useIntl()
  const valueNights = { nights }
  const valuePricePerNighs = useMemo(
    () => ({
      amount: <Price amount={Math.round(subtotal / nights)} currency={subtotalCurrency} />,
    }),
    [nights, subtotal, subtotalCurrency]
  )

  return nights ? (
    <Line variant={showDiscount ? 'nopadding' : undefined} data-testid="subtotal">
      <LineRoomResume>
        <p>{intl.formatMessage({ id: 'global.nights' }, valueNights)}</p>
        {showDiscount ? (
          <span>
            {intl.formatMessage({ id: 'global.price-per-night-including' }, valuePricePerNighs)}
          </span>
        ) : null}
      </LineRoomResume>
      <Amount>
        <Price amount={subtotal} currency={subtotalCurrency} />
      </Amount>
    </Line>
  ) : null
}

interface LineDiscountProps {
  bighead: number
  isPrime: boolean
  isVoucher: boolean
  paymentPolicy: string
  sellingRate: number
  sellingRateCurrency: string
  showDiscount: boolean
}

const LineDiscount = ({
  bighead,
  isPrime,
  isVoucher,
  paymentPolicy,
  sellingRate,
  sellingRateCurrency,
  showDiscount,
}: LineDiscountProps) => {
  const intl = useIntl()
  const { data: promo } = useUseCase('GetCurrentPromoMarketingUseCase')

  const isRatesActive = promo && !!promo?.tag

  const discount = sellingRate - bighead
  const valuePercent = {
    discount: `-${calculateDiscount(sellingRate, bighead)}%`,
    amount: (
      <span>
        <Price amount={discount} currency={sellingRateCurrency} />
      </span>
    ),
  }

  return discount < 0 && showDiscount ? (
    <Line data-testid="discount">
      <DiscountText prime={isPrime}>
        {!isVoucher && isRatesActive && paymentPolicy !== 'NOR' ? (
          <PromoText color="#444">{intl.formatMessage({ id: promo.tag.text })}: </PromoText>
        ) : null}
        {intl.formatMessage({ id: 'global.discount-off' }, valuePercent)}
      </DiscountText>
    </Line>
  ) : null
}

interface LineStaywalletProps {
  staywallet: number | null
  staywalletCurrency: string
  variant: (typeof VARIANTS)[number] | null
}

export const LineStaywallet = ({
  staywallet,
  staywalletCurrency,
  variant,
}: LineStaywalletProps) => {
  const intl = useIntl()

  return staywallet && variant !== VARIANT_SIMPLE ? (
    <Line data-testid="staywallet" variant="subtract">
      <p>{intl.formatMessage({ id: 'staywallet.credit' })}</p>
      <Amount>
        <Price amount={-staywallet} currency={staywalletCurrency} />
      </Amount>
    </Line>
  ) : null
}
interface LineInsuranceProps {
  insurance: number | null
  insuranceCurrency: string
  variant: (typeof VARIANTS)[number] | null
}

export const LineInsurance = ({ insurance, insuranceCurrency, variant }: LineInsuranceProps) => {
  const intl = useIntl()
  return insurance && variant !== VARIANT_SIMPLE ? (
    <Line>
      <p>{intl.formatMessage({ id: 'insurance.refund-protect' })}</p>
      <Amount>
        <Price amount={insurance} currency={insuranceCurrency} />
      </Amount>
    </Line>
  ) : null
}

interface ILinePriceDropProtectProps {
  pdp: number | null
  pdpCurrency: string
  variant: (typeof VARIANTS)[number] | null
}
export const LinePriceDropProtect = ({ pdp, pdpCurrency, variant }: ILinePriceDropProtectProps) => {
  const intl = useIntl()
  return pdp && variant !== VARIANT_SIMPLE ? (
    <Line>
      <p>{intl.formatMessage({ id: 'insurance.price-drop-protection' })}</p>
      <Amount>
        <Price amount={pdp} currency={pdpCurrency} />
      </Amount>
    </Line>
  ) : null
}

export interface LineAdditionalFeesProps {
  additionalFees?: AdditionalFeesType[]
  isVoucher: boolean
  simple?: boolean
}

export const LineAdditionalFees = ({
  additionalFees,
  isVoucher,
  simple = false,
}: LineAdditionalFeesProps) => {
  const intl = useIntl()
  if (!additionalFees) {
    return null
  }

  const additionalFeesWithAmount = additionalFees.filter(additionalFee => additionalFee.amount > 0)

  if (additionalFeesWithAmount.length < 1) {
    return null
  }

  const totalAdditionalFees = additionalFeesWithAmount.reduce((totalAddFees, currentAddFees) => {
    if (!totalAddFees) {
      return currentAddFees
    }

    const amount = totalAddFees.amount + currentAddFees.amount

    let local
    if (totalAddFees?.local?.amount && currentAddFees?.local?.amount) {
      const localAmount = totalAddFees?.local?.amount + currentAddFees?.local?.amount
      local = { ...totalAddFees?.local, amount: localAmount }
    }

    return {
      ...currentAddFees,
      ...totalAddFees,
      amount,
      ...(currentAddFees?.local?.amount && { local }),
    }
  })

  const amount = isVoucher
    ? (totalAdditionalFees?.local?.amount ?? totalAdditionalFees.amount)
    : totalAdditionalFees.amount

  const roundedAmount = Math.round(amount)

  const currency = isVoucher
    ? (totalAdditionalFees?.local?.currency ?? totalAdditionalFees.currency)
    : totalAdditionalFees.currency

  return (
    <LineAdditionalFeesBase data-testid="additionalFees">
      <div>
        {simple ? (
          <>
            <p>{intl.formatMessage({ id: 'global.due-at-property' })}</p>
            <Note>{intl.formatMessage({ id: 'global.charged-in-arrival' })}</Note>
          </>
        ) : (
          <>
            <p>{intl.formatMessage({ id: 'global.destination-fee' })}</p>
            <Note>{intl.formatMessage({ id: 'global.due-at-property' })}</Note>
          </>
        )}
      </div>
      <Amount>
        <Price amount={isVoucher ? amount : roundedAmount} currency={currency} />
      </Amount>
    </LineAdditionalFeesBase>
  )
}
interface LineTotalPriceProps {
  total: number
  totalCurrency: string
  className?: string
}

export const LineTotalPrice = ({ total, totalCurrency, className }: LineTotalPriceProps) => {
  const intl = useIntl()
  return (
    <Line className={className} data-testid="total" variant="total">
      <p>{intl.formatMessage({ id: 'global.total' })}</p>
      <Amount>
        <Price amount={total >= 0 ? Math.round(total) : 0} currency={totalCurrency} />
      </Amount>
    </Line>
  )
}

export interface PriceDetailsProps {
  additionalFees?: AdditionalFeesType[]
  bighead: number
  insurance: number | null
  insuranceCurrency: string
  isPrime: boolean
  isVoucher: boolean
  nights: number
  payDate: string
  payLate: number | null
  payNow: number
  paymentPolicy: string
  pdp: number | null
  pdpCurrency: string
  sellingRate: number
  sellingRateCurrency: string
  staywallet: number | null
  staywalletCurrency: string
  subtotal: number
  subtotalCurrency: string
  taxes: number
  taxesCurrency: string
  total: number
  totalCurrency: string
  totalWithFees?: number
  variant: (typeof VARIANTS)[number] | null
  showDiscount?: boolean
  showPaymentDates?: boolean
  showWalletCheckbox?: boolean
  purchaseId?: string
}

const PriceDetails = ({
  additionalFees,
  bighead,
  insurance,
  insuranceCurrency,
  isPrime,
  isVoucher,
  nights,
  payDate,
  payLate,
  payNow,
  paymentPolicy,
  pdp,
  pdpCurrency,
  purchaseId,
  sellingRate,
  sellingRateCurrency,
  showDiscount = true,
  showPaymentDates = true,
  showWalletCheckbox,
  staywallet,
  staywalletCurrency,
  subtotal,
  subtotalCurrency,
  taxes,
  taxesCurrency,
  total,
  totalCurrency,
  totalWithFees,
  variant,
}: PriceDetailsProps) => {
  const intl = useIntl()

  const dateText = intl.formatDate(payDate, { year: 'numeric', month: 'long', day: '2-digit' })
  const valuePayDate = { date: dateText }

  return (
    <PriceDetailsBase data-testid="price-details">
      {variant !== VARIANT_SIMPLE && (
        <Title>
          {intl.formatMessage({
            id: isVoucher ? 'global.price' : 'global.price-breakdown',
          })}
        </Title>
      )}

      {showWalletCheckbox && purchaseId && <CheckoutWalletSelector purchaseId={purchaseId} />}

      <LineRoom
        nights={nights}
        subtotal={subtotal}
        subtotalCurrency={subtotalCurrency}
        showDiscount={showDiscount}
      />

      <LineDiscount
        bighead={bighead}
        isPrime={isPrime}
        isVoucher={isVoucher}
        paymentPolicy={paymentPolicy}
        sellingRate={sellingRate}
        sellingRateCurrency={sellingRateCurrency}
        showDiscount={showDiscount}
      />

      <LineTaxes taxes={taxes} taxesCurrency={taxesCurrency} />
      {!isVoucher ? (
        <LineAdditionalFees additionalFees={additionalFees} isVoucher={isVoucher} />
      ) : null}

      <LineStaywallet
        staywallet={staywallet}
        staywalletCurrency={staywalletCurrency}
        variant={variant}
      />
      <LineInsurance
        insurance={insurance}
        insuranceCurrency={insuranceCurrency}
        variant={variant}
      />

      <LinePriceDropProtect pdp={pdp} pdpCurrency={pdpCurrency} variant={variant} />

      <LineTotalPrice total={totalWithFees ?? total} totalCurrency={totalCurrency} />

      {isVoucher ? (
        <LineAdditionalFees additionalFees={additionalFees} isVoucher={isVoucher} />
      ) : null}

      {variant !== VARIANT_SIMPLE && payNow >= 0 && payNow !== (totalWithFees ?? total) ? (
        <Line variant="rate">
          <p>{intl.formatMessage({ id: 'global.now-pay' })}</p>
          <Amount>
            <Price amount={payNow} currency={sellingRateCurrency} />
          </Amount>
        </Line>
      ) : null}

      {variant !== VARIANT_SIMPLE && showPaymentDates && payLate && payDate ? (
        <Line>
          <p>{intl.formatMessage({ id: 'global.you-will-pay' }, valuePayDate)}</p>
          <Amount>
            <Price amount={payLate} currency={sellingRateCurrency} />
          </Amount>
        </Line>
      ) : null}

      <MessageBox message={intl.formatMessage({ id: 'global.currency-charge' })} type="info" />
    </PriceDetailsBase>
  )
}

export default PriceDetails
